import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@smarty/shared';
import { supabase } from '@/lib/supabase';
import {
  dondeSeCreaElNegocio, dondeSePoneLaClave, miCuentaTieneClave, miInvitacionDeNegocio, type MiInvitacion,
} from '@/lib/elAltaInvitada';
import {
  Page,
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Box,
  Spinner,
  Icon,
} from '@/lib/polaris';
import { AlertCircleIcon } from '@/lib/polaris-icons';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Layout alternativo durante carga (p. ej. AdminLoadingLayout con TopBar + logo). Evita barra negra al refrescar. */
  loadingLayout?: React.ReactNode;
}

/** Un negocio del usuario, con su estado — incluidos los dados de baja. */
type MiNegocio = { nombre: string; estado: string; baja_desde: string | null; baja_motivo: string | null };

export function ProtectedRoute({ children, loadingLayout }: ProtectedRouteProps) {
  const { session, tenantId, loading, signOut } = useAuth();

  /*
   * ⭐ POR QUÉ SE PREGUNTA EL MOTIVO. Cuando un negocio se da de baja, la base deja de
   *    devolverlo en `auth_user_tenant_ids()` — así se corta el acceso de verdad, en la base y no
   *    en la pantalla. El efecto colateral es que el usuario cae acá, en «no tenés negocio», y ese
   *    cartel MIENTE: su negocio existe, fue dado de baja.
   *
   *    Y el motivo cambia lo que la persona hace. «No estás asociado a nada» invita a pedir que le
   *    arreglen los permisos; «tu cuenta fue dada de baja el 24 de agosto» dice qué pasó y a quién
   *    reclamarle.
   *
   * 🪤 Se consulta SÓLO cuando ya se sabe que no hay negocio activo. Preguntarlo siempre sería un
   *    viaje a la base en cada carga del panel para una respuesta que casi nunca cambia nada.
   */
  const [suspendido, setSuspendido] = useState<MiNegocio | null>(null);
  /*
   * ⭐ Y SI TIENE UNA INVITACIÓN PENDIENTE, VA A CREAR SU NEGOCIO (founder, 30-sep). Quien ya tenía cuenta
   *    —por ejemplo, porque se eliminó su negocio— recibe un correo que lo manda al login, y el login lo
   *    trae acá: sin esto chocaba con «Cuenta sin acceso» teniendo la invitación esperándolo.
   *    `undefined` = todavía no se sabe; mientras tanto no se pinta el cartel.
   */
  const [invitacion, setInvitacion] = useState<MiInvitacion | null | undefined>(undefined);
  /*
   * 🚨 PERO PRIMERO, SU CONTRASEÑA (20261028031500). El enlace de la invitación abre la sesión sin clave, y
   *    este guardia llevaba directo a crear el negocio: el 27-oct dos drops crearon su tienda así y al volver
   *    no tenían con qué entrar. Se pregunta junto con la invitación, para decidir el destino de una vez.
   */
  const [tieneClave, setTieneClave] = useState<boolean | null>(null);
  useEffect(() => {
    if (loading || !session || tenantId) return;
    let vigente = true;
    void supabase.rpc('fn_estado_de_mis_negocios').then(({ data }) => {
      if (!vigente || !Array.isArray(data)) return;
      const dado = (data as MiNegocio[]).find((n) => n.estado === 'suspendida');
      if (dado) setSuspendido(dado);
    });
    void miInvitacionDeNegocio().catch(() => null).then(async (inv) => {
      const clave = inv ? await miCuentaTieneClave().catch(() => null) : null;
      if (!vigente) return;
      setTieneClave(clave);
      setInvitacion(inv);
    });
    return () => { vigente = false; };
  }, [loading, session, tenantId]);

  if (loading || (session && !tenantId && invitacion === undefined)) {
    if (loadingLayout) {
      return <>{loadingLayout}</>;
    }
    return (
      <Page>
        <Box padding="800">
          <InlineStack align="center">
            <BlockStack gap="300" align="center">
              <Spinner size="large" />
              <Text variant="bodyMd" tone="subdued" as="p">
                Cargando...
              </Text>
            </BlockStack>
          </InlineStack>
        </Box>
      </Page>
    );
  }

  // Si no hay sesión, redirigir a login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!tenantId && invitacion) {
    return <Navigate to={tieneClave === false ? dondeSePoneLaClave(invitacion) : dondeSeCreaElNegocio(invitacion)} replace />;
  }

  // Si hay sesión pero no hay tenant_id, mostrar mensaje de error
  if (!tenantId) {
    return (
      <Page>
        <Box padding="800">
          <InlineStack align="center">
            <Card>
              <BlockStack gap="400">
                <InlineStack gap="200" align="center">
                  <Icon source={AlertCircleIcon} tone="critical" />
                  <Text variant="headingMd" fontWeight="semibold" as="h2">
                    {suspendido ? 'Tu cuenta fue dada de baja' : 'Cuenta sin acceso'}
                  </Text>
                </InlineStack>
                {/*
                  🪤 «No estás asociado a ningún restaurante» era lo único que se decía, y para el
                     dado de baja era falso: su negocio existe y sus datos están intactos. Además
                     hablaba de «restaurante», de la época en que Smarty era sólo eso.
                */}
                <Text variant="bodyMd" tone="subdued" as="p">
                  {suspendido
                    ? `«${suspendido.nombre}» fue dado de baja${
                        suspendido.baja_desde
                          ? ` el ${new Date(suspendido.baja_desde).toLocaleDateString('es-PE', { day: 'numeric', month: 'long' })}`
                          : ''
                      }.`
                    : 'Tu cuenta todavía no está asociada a ningún negocio.'}
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  {suspendido
                    ? 'Tu información sigue guardada. Si quieres volver, escríbenos y la reactivamos tal como la dejaste.'
                    : 'Escríbenos para que te demos acceso o, si vendes por dropshipping, crea tu cuenta Drop desde el registro.'}
                </Text>
                <Button
                  onClick={async () => {
                    await signOut();
                    window.location.href = '/login';
                  }}
                  variant="secondary"
                >
                  Cerrar sesión
                </Button>
              </BlockStack>
            </Card>
          </InlineStack>
        </Box>
      </Page>
    );
  }

  return <>{children}</>;
}
